import { createReducer, on } from '@ngrx/store';
import { DateTime } from 'luxon';
import { AchActions, CheckActions, InternalTransferActions, InternalTransfersActions, TransactionFormActions, WireActions, MasterStatusViewActions, ReviewPageActions } from '../../create-request/actions';
import { EAuthModelCustomer } from '../../create-request/models/internal-transfer/EAuthModelCustomer';
import { Constants, sloaAuthorizationsAccessList } from '../../shared/utilities/Constants';
import {
  StandingAuthAchActions,
  StandingAuthCheckActions,
  StandingAuthInternalTransferActions,
  StandingAuthMasterStatusViewActions,
  StandingAuthWireActions
} from '../../standing-authorizations/actions';
import * as AccountInfoActions from '../actions/account-info.actions';
import { Customers } from '../models/AccountInfoApiResponse';
import { AccountInfoState } from '../models/AccountInfoState';
import { InitReleaseActions } from '../../initiate-and-release/actions';
import { ViewPageActions } from '../../transaction-view/actions';

export const infoKey = 'info';

export interface State extends AccountInfoState { }

export const initialAccountInfoState: State = {
  accountId: '',
  registrationType: '',
  categoryType: '',
  subAccountNameLine1: '',
  subAccountNameLine2: '',
  subAccountNameLine3: '',
  classifications: {
    isIra: false,
    isPtrs: false,
    isIip: false,
    isNraAccount: false,
    isPreview: false,
    isDbsUser: false,
    isEmployee: false,
    isDemoUser: false,
    isFsaUser: false,
    isTestamentaryTrust: false,
    isEstate: false,
    isGuardian: false,
    isConserv: false,
    hasBundledOffer: false,
  },
  eauthorization: { areAllAccountHoldersIneligible: false, isEauthEligible: false, numberOfAccountHolders: 0, customers: [], isAccountRestricted: false, isOrgAccount: false, restrictionReason: [] },
  updated: DateTime.now().toString(),
  masterAccount: '',
  acknowledgementText: '',
  formerTdaIndicator: false,
  overviewFailed: false,
  accountInfoCallSuccess: false,
  hasStandingAuthorizationAccess: false,
  userName: '',
  userContext: '',
  isBtMasterOnly: false,
  primaryMaster: '',
  pfaDetails: {
    hasLpfaRole: false,
    hasNdfaRole: false,
    hasWpfaRole: false,
    hasCustodyWPFARole: false,
    hasBSLOAWPFARole: false
  }
};

export const accountInfoReducer = createReducer(
  initialAccountInfoState,
  on(AccountInfoActions.updateSubAccount, (state, action) => {
    return {
      ...state,
      accountId: action.accountId,
      eauthorization: initialAccountInfoState.eauthorization,
      accountInfoCallSuccess: false
    }
  }),
  on(AccountInfoActions.updateMasterAccount, (state, action) => {
    return {
      ...state,
      masterAccount: action.masterAccount,
      accountInfoCallSuccess: false
    }
  }),
  on(AccountInfoActions.updateUserContext, (state, { context}) => {
    return {
      ...state,
      userContext: context,
      accountInfoCallSuccess: false
    }
  }),
  on(MasterStatusViewActions.callTransactionsSuccess, (state) => {
    return {
      ...state,
      updated: DateTime.now().toString()
    }
  }),
  on(StandingAuthMasterStatusViewActions.callLettersSuccess, (state) => {
    return {
      ...state,
      updated: DateTime.now().toString()
    }
  }),
  on(AccountInfoActions.loadAccountInfoSuccess, (state, accountInfo) => {
  return {
    ...state,
    accountId: accountInfo.accountId,
    registrationType: accountInfo.registrationType,
    categoryType: accountInfo.categoryType,
    subAccountNameLine1: accountInfo.subAccountNameLine1,
    subAccountNameLine2: accountInfo.subAccountNameLine2,
    subAccountNameLine3: accountInfo.subAccountNameLine3,
    classifications: {
      ...state.classifications,
      ...accountInfo.classifications
    },
    eauthorization: accountInfo.eauthorization,
    isDemoUser: accountInfo.classifications.isDemoUser,
    isFsaUser: accountInfo.classifications.isFsaUser,
    updated: accountInfo.updated,
    formerTdaIndicator: accountInfo.formerTdaIndicator,
    accountInfoCallSuccess: true
  }
}),
  on(AccountInfoActions.loadPalAccountInfoSuccess, (state, { accountTitle }) => {
    return {
      ...state,
      registrationType: accountTitle.regShortName,
      subAccountNameLine1: accountTitle.line1,
      subAccountNameLine2: accountTitle.line2,
      subAccountNameLine3: accountTitle.line3,
      accountInfoCallSuccess: true
    }
  }),

  on(AchActions.inquiryCallSuccessful,StandingAuthAchActions.inquiryCallSuccessful, (state, { achInquiryResponse }) => {
    return {
      ...state,
      accountId: achInquiryResponse.accountRegistration.subAccountNumber.replace(/[^\d]/g, ''),
      registrationType: achInquiryResponse.accountRegistration.regShortName,
      subAccountNameLine1: achInquiryResponse.accountRegistration.subAccountNameLine1,
      subAccountNameLine2: achInquiryResponse.accountRegistration.subAccountNameLine2,
      subAccountNameLine3: achInquiryResponse.accountRegistration.subAccountNameLine3,
      masterAccount: achInquiryResponse.accountRegistration.masterAccount.replace(/[^\d.]/g, ''),
      classifications: {
        ...state.classifications,
        isPtrs: achInquiryResponse.trustFields?.isTrust,
        isIra: achInquiryResponse.iraFields?.isIraTransaction
      }
    }
  }),

  on(AccountInfoActions.loadIsDbsUserAndIsEmployee, (state, accountInfo) => {
    return {
      ...state,
      classifications: {
        ...state.classifications,
        isDbsUser: accountInfo.isDbsUser,
        isEmployee: accountInfo.isEmployee
      }
    }
  }),

  on(AccountInfoActions.loadUsername, (state, accountInfo) => {
    return {
      ...state,
      userName: accountInfo.userName,
      accountInfoCallSuccess: false
    }
  }),

  on(AchActions.inputCallSuccessful, (state, inputResponse) => {
    return {
      ...state,
      classifications: {
        ...state.classifications,
        isIra: inputResponse.achInfo.isIraTransaction
      },
    }
  }),
  on(CheckActions.verifyCallSuccessful, StandingAuthCheckActions.verifyCallSuccessful, (state, response) => {
    return {
      ...state,
      acknowledgementText: response.checkVerifyResponse.acknowledgementArtifactContent
    }
  }),
  on(TransactionFormActions.loadChecksReviewPageWithoutVerifyCall,
    TransactionFormActions.loadInternalTransferWithoutVerifyCall,
    StandingAuthInternalTransferActions.loadInternalTransferReviewPageWithoutVerifyCall,
    StandingAuthCheckActions.loadChecksReviewPageWithoutVerifyCall,
    StandingAuthWireActions.loadWiresReviewPageWithoutVerifyCall,
    (state, response) => {
    return {
      ...state,
      acknowledgementText: Constants.moneyMovementAcknowledgeText + '\n\n' + Constants.moneyMovementAcknowledgeNote
    }
  }),
  on(AccountInfoActions.loadTransactions, (state, response) => {
    return {
      ...state,
      overviewFailed: false
    }
  }),
  on(AccountInfoActions.loadTransactionsSuccess, (state, response) => {
    return {
      ...state,
      masterAccount: response.accountDisplayDetails?.masterAccount,
      classifications: {
        ...state.classifications,
        isIip: response.isIip,
        isDigitalToPdf: response.isDigitalToPdf,
        isCustomer59OrOlder: response.rmdDetails?.cusAge59Indicator,
        isRmdEligible: response.rmdDetails?.cusAge70Indicator,
        isNraAccount: response.iraDetails?.nraAccountIndicator ?? false,
      },
      pfaDetails: {
        hasLpfaRole: response.pfaDetails?.hasLpfaRole,
        hasNdfaRole: response.pfaDetails?.hasNdfaRole,
        hasWpfaRole: response.pfaDetails?.hasWpfaRole,
        hasCustodyWPFARole: response.pfaDetails?.hasCustodyWPFARole,
        hasBSLOAWPFARole: response.pfaDetails?.hasBSLOAWPFARole
      } ,
      overviewFailed: false,
      hasStandingAuthorizationAccess: sloaAuthorizationsAccessList.some(val => response.transactionAuthorizationDetails?.sloaAuthorization?.includes(val)),
      isBtMasterOnly: response.isBtMasterOnly,
      primaryMaster: response.primaryMaster
    }
  }),
  on(AccountInfoActions.loadTransactionsFailure, (state, response) => {
    return {
      ...state,
      overviewFailed: true
    }
  }),
  on(AchActions.verifyCallSuccessful, StandingAuthAchActions.verifyCallSuccessful, (state, {response}) => {
    return {
      ...state,
      acknowledgementText: response.acknowledgementArtifactContent
    }
  }),
  on(InternalTransferActions.verifyCallSuccessful, StandingAuthInternalTransferActions.verifyCallSuccessful, (state, { type, ...action }) => {
    const { response } = action;
    return {
      ...state,
      acknowledgementText: response.acknowledgementArtifactContent,
      eauthorization: {
        ...state.eauthorization,
        customers: mapEAuthCustomers(state.eauthorization.customers, response.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList)
      }
    }
  }),
  on(InternalTransferActions.verifyCallFinishedWithError, (state, action) => {
    const response = action.response;
    return {
        ...state,
        acknowledgementText: response.acknowledgementArtifactContent,
    }
  }),
  on(TransactionFormActions.continueClickTotalDist, (state, response) => {
    return {
      ...state,
      classifications: {
        ...state.classifications,
        isDigitalToPdf: true
      },
      acknowledgementText: Constants.moneyMovementAcknowledgeText + '\n\n' + Constants.moneyMovementAcknowledgeNote
    }
  }),
  on(ReviewPageActions.editClicked, ReviewPageActions.cancelClicked, (state, action) => {
    return {
      ...state,
      classifications: {
      ...state.classifications,
      isDigitalToPdf: false
      }
    }
  }),
  on(InternalTransfersActions.verifyListCallSuccessful, InternalTransfersActions.verifyListCallFinishedWithError, (state, action) => {
    const response = action.response;
    return {
        ...state,
        acknowledgementText: response.included[0].attributes.acknowledgementArtifactContent,
    }
  }),
  on(WireActions.verifyV3CallSuccessful, StandingAuthWireActions.verifyV3CallSuccessful, (state, { response }) => {
    return {
      ...state,
      acknowledgementText: response.acknowledgementArtifactContent
    }
  }),
  on(StandingAuthCheckActions.verifyCallSuccessful, StandingAuthCheckActions.verifyCallFinishedWithError, (state, {checkVerifyResponse}) => {
    return {
      ...state,
      acknowledgementText: checkVerifyResponse.acknowledgementArtifactContent
    }
  }),
  on(WireActions.inquiryCallSuccessful, (state, response) => {
    const { wireInquiryResponse } = response;
    return {
      ...state,
      classifications: {
        ...state.classifications,
        isPtrs: wireInquiryResponse.isTrust,
        isIra: wireInquiryResponse.isIRATransaction
      },
      registrationType: wireInquiryResponse.accountDisplayRecord.regShortName,
      accountId: wireInquiryResponse.accountDisplayRecord.subAccountNumber.replace(/[^\d]/g, ''),
      subAccountNameLine1: wireInquiryResponse.accountDisplayRecord.subAccountNameLine1,
      subAccountNameLine2: wireInquiryResponse.accountDisplayRecord.subAccountNameLine2,
      subAccountNameLine3: wireInquiryResponse.accountDisplayRecord.subAccountNameLine3,
      masterAccount: wireInquiryResponse.accountDisplayRecord.masterAccount.replace(/[^\d.]/g, ''),
    }
  }),
  on(InternalTransferActions.inquiryCallSuccessful, (state, {internalTransferInquiryResponse}) => {
    return {
      ...state,
      registrationType: internalTransferInquiryResponse.accountInfoBanner.regShortName,
      subAccountNameLine1: internalTransferInquiryResponse.accountInfoBanner.subAccountNameLine1,
      subAccountNameLine2: internalTransferInquiryResponse.accountInfoBanner.subAccountNameLine2,
      subAccountNameLine3: internalTransferInquiryResponse.accountInfoBanner.subAccountNameLine3,
      classifications: {
        ...state.classifications,
        isPtrs: internalTransferInquiryResponse.trustFields.isTrust
      }
    }
  }),
  on(CheckActions.inquiryCallSuccessful, (state, {checkInquiryResponse}) => {
    return {
      ...state,
      registrationType: checkInquiryResponse.cashieringCheckInputDetails.accountDisplayRecord.regShortName,
      subAccountNameLine1: checkInquiryResponse.cashieringCheckInputDetails.accountDisplayRecord.subAccountNameLine1,
      subAccountNameLine2: checkInquiryResponse.cashieringCheckInputDetails.accountDisplayRecord.subAccountNameLine2,
      subAccountNameLine3: checkInquiryResponse.cashieringCheckInputDetails.accountDisplayRecord.subAccountNameLine3,
      classifications: {
        ...state.classifications,
        isPtrs: checkInquiryResponse.cashieringCheckInputDetails.cashieringAccountRefDetails.isTrust
      }
    }
  }),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response }) => {
    return {
      ...state,
      masterAccount: response.masterAccount,
      accountId: response.subAccount,
      classifications: {
        ...state.classifications,
        isIra: response.transactionType !== 'moneylink' ? 
                (!!response.draftObject.IRAAccount || !!response.draftObject.isIraTransaction || !! response.draftObject.iraAccount): 
                state.classifications.isIra,
        isPtrs: response.accountData.isTrust,
        isRmdEligible: response.accountData.isRmdEligible,
      },
      taxes:{
        state: response.accountData.stateCd
      }
    }
  }),
  on(ViewPageActions.backButtonClickedFromMsv, (state) => {
    return {
      ...state,
      registrationType: '',
      subAccountNameLine1: '',
      subAccountNameLine2: '',
      subAccountNameLine3: '',
      classifications: {
        ...state.classifications,
        isPtrs: false
      }
    }
  }),
);

export function mapEAuthCustomers(accountInfoCustomers: Customers[], verifyResponseCustomers: EAuthModelCustomer[]): Customers[] {
  if (accountInfoCustomers && accountInfoCustomers.length > 0) {
    return accountInfoCustomers.map(accountInfoCustomer => {
      for (let verifyResponseCustomer of verifyResponseCustomers) {
        if (accountInfoCustomer.customerId === verifyResponseCustomer.customerId) {
          return {
            isAccountWebEnabled: verifyResponseCustomer.isAccountWebEnabled,
            customerId: verifyResponseCustomer.customerId,
            isCustomerWebEnrolled: verifyResponseCustomer.isCustomerWebEnrolled,
            emailId: verifyResponseCustomer.emailId,
            firstName: verifyResponseCustomer.firstName,
            lastName: verifyResponseCustomer.lastName,
            loginIdEnrollmentStatus: verifyResponseCustomer.loginIdEnrollStatus,
            isEauthEligible: verifyResponseCustomer.isCustomerEAuthEligible,
            displayIndicator: verifyResponseCustomer.isDisplayAllowed,
            organizationName: verifyResponseCustomer.organizationName,
            accountRoles: verifyResponseCustomer.accountRoles,
            restrictionReason: verifyResponseCustomer.customerRestrictionReason,
            typeCode: verifyResponseCustomer.typeCode
          }
        }
        return accountInfoCustomer;
      }
      return accountInfoCustomer;
    })
  }
  return [];
}
